// WHAT: Modal component for creating new aspects
// WHY:  Encapsulates aspect creation UI in reusable component
// DOMAIN: Settings → TimeTracQ → Workspace Management
// DEPENDENCIES: UI components (Modal, Input, Label, Select, Button, Stack)
// RISKS:
//   - WARN: No validation of aspect name format or uniqueness within workspace

import { Modal, Input, Label, Select, Button, Stack } from "@/components/ui";

/**
 * WHAT:
 *   Modal component for adding a new aspect to a workspace.
 *
 * WHY:
 *   Provides reusable UI for aspect creation with form validation.
 *
 * PROPS:
 *   - isOpen: Whether the modal is open
 *   - onClose: Callback to close the modal
 *   - workspaceNames: Array of available workspace names
 *   - aspectData: Current aspect form data
 *   - onAspectDataChange: Callback when aspect data changes
 *   - onAdd: Callback to create the aspect
 *   - saving: Whether the save operation is in progress
 *
 * BEHAVIOR:
 *   - Validates that workspace and aspect name are selected/entered
 *   - Disables submit button when saving or required fields are missing
 */
interface AddAspectModalProperties {
  aspectData: {
    workspaceName: string;
    aspectName: string;
    hours: number;
    status: string;
    color: string;
    carryover: boolean;
    count_hours: boolean;
    count_plan: boolean;
  };
  isOpen: boolean;
  onAdd: () => void;
  onAspectDataChange: (data: Partial<AddAspectModalProperties["aspectData"]>) => void;
  onClose: () => void;
  saving: boolean;
  workspaceNames: string[];
}

/**
 * WHAT:
 *   React component that renders a modal for adding a new TimeTracQ aspect.
 *
 * WHY:
 *   Encapsulates the UI and interaction logic for the "Add Aspect" functionality.
 *
 * @param root0
 *
 * @param root0.aspectData
 *
 * @param root0.isOpen
 *
 * @param root0.onAdd
 *
 * @param root0.onAspectDataChange
 *
 * @param root0.onClose
 *
 * @param root0.saving
 *
 * @param root0.workspaceNames
 *
 * @returns {JSX.Element} Modal component for adding a new aspect
 *
 */
export function AddAspectModal({
  aspectData,
  isOpen,
  onAdd,
  onAspectDataChange,
  onClose,
  saving,
  workspaceNames,
}: Readonly<AddAspectModalProperties>) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Aspect">
      <Stack direction="col" gap={4}>
        <Stack direction="col" gap={2}>
          <Label>Workspace</Label>
          <Select
            onChange={(value) => {
              const workspaceValue = Array.isArray(value) ? value[0] : value;
              if (typeof workspaceValue === "string" && workspaceValue) {
                onAspectDataChange({ workspaceName: workspaceValue });
              }
            }}
            options={workspaceNames.map((name) => ({
              label: name,
              value: name,
            }))}
            placeholder="Select workspace..."
            size="md"
            value={aspectData.workspaceName}
          />
        </Stack>

        <Stack direction="col" gap={2}>
          <Label>Aspect Name</Label>
          <Input
            onChange={(e) => {
              onAspectDataChange({ aspectName: e.target.value });
            }}
            placeholder="Enter aspect name..."
            size="md"
            type="text"
            value={aspectData.aspectName}
          />
        </Stack>

        <div className="grid grid-cols-2 gap-4">
          <Stack direction="col" gap={2}>
            <Label>Hours</Label>
            <Input
              min="0"
              onChange={(e) => {
                onAspectDataChange({ hours: Number.parseFloat(e.target.value) || 0 });
              }}
              size="md"
              step="0.5"
              type="number"
              value={aspectData.hours}
            />
          </Stack>

          <Stack direction="col" gap={2}>
            <Label>Status</Label>
            <Select
              onChange={(value) => {
                const statusValue = Array.isArray(value) ? value[0] : value;
                if (typeof statusValue === "string" && statusValue) {
                  onAspectDataChange({ status: statusValue });
                }
              }}
              options={[
                { label: "Include", value: "Include" },
                { label: "Exclude", value: "Exclude" },
                { label: "Inactive", value: "Inactive" },
              ]}
              size="md"
              value={aspectData.status}
            />
          </Stack>
        </div>

        <Stack direction="col" gap={2}>
          <Label>Color</Label>
          <div className="flex items-center space-x-2">
            <input
              className="w-12 h-10 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              onChange={(e) => {
                onAspectDataChange({ color: e.target.value });
              }}
              type="color"
              value={aspectData.color}
            />
            <span className="text-sm text-slate-400">{aspectData.color}</span>
          </div>
        </Stack>

        <div className="flex justify-end space-x-3">
          <Button onClick={onClose} size="md" variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={saving || !aspectData.workspaceName || !aspectData.aspectName.trim()}
            onClick={onAdd}
            size="md"
            variant="primary"
          >
            {saving ? "Creating..." : "Create Aspect"}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}
